/**
 * Folding earlier history when a Build is reopened (FRONT-3175).
 *
 * Reopening a long session used to land on the whole tape. Now everything
 * before the last exchange that existed when the window opened folds into one
 * line under EDDY's exec summary; a click unfolds it. The fold line is fixed
 * at the moment of opening, so whatever happens during this visit renders
 * normally and never slides into the fold.
 *
 * Two things never fold: the last exchange (the newest message the person had
 * sent, and everything after it), and an open question card — a card nobody
 * can see is a run nobody can unblock.
 */

import type { GroupedTranscriptItem } from './activity-runs';

export interface HistoryFold {
  hidden: GroupedTranscriptItem[];
  visible: GroupedTranscriptItem[];
  runs: number;
  steps: number;
}

export function foldEarlierHistory(
  items: GroupedTranscriptItem[],
  openedAtSeq: number | null,
): HistoryFold | null {
  if (openedAtSeq == null) return null;

  let boundary: number | null = null;
  for (const item of items) {
    if (item.kind === 'user' && item.seq <= openedAtSeq) boundary = item.seq;
  }
  if (boundary == null) return null;

  // Only the newest card accepts input (FRONT-707); an open one stays visible.
  let newestQuestion: Extract<GroupedTranscriptItem, { kind: 'questions' }> | null = null;
  for (const item of items) if (item.kind === 'questions') newestQuestion = item;
  if (newestQuestion && !newestQuestion.outcome && newestQuestion.seq < boundary) {
    boundary = newestQuestion.seq;
  }

  const hidden = items.filter((item) => item.seq < boundary);
  let runs = 0;
  let steps = 0;
  for (const item of hidden) {
    if (item.kind === 'marker' && item.variant === 'started') runs += 1;
    else if (item.kind === 'activity') steps += 1;
    else if (item.kind === 'activity-run') steps += item.items.length;
  }
  // A lone first message with no work after it is not history worth a fold.
  if (runs === 0) return null;

  return { hidden, visible: items.filter((item) => item.seq >= boundary), runs, steps };
}

/** The fold line's words, e.g. "Earlier: 5 runs · 180 steps". */
export function foldLabel(fold: Pick<HistoryFold, 'runs' | 'steps'>): string {
  const parts = [`${fold.runs} ${fold.runs === 1 ? 'run' : 'runs'}`];
  if (fold.steps > 0) parts.push(`${fold.steps} ${fold.steps === 1 ? 'step' : 'steps'}`);
  return `Earlier: ${parts.join(' · ')}`;
}
